<script setup>
import { computed, onMounted, ref } from "vue";

import { format, pSBC } from "../../lib/helpers";

import BaseNotificationIcon from "./BaseNotificationIcon.vue";
import importedProps from "./config/notificationProps";

const props = defineProps({ ...importedProps });
const emit = defineEmits(["close"]);

const timer = ref(null);
const start = ref(null);
const remaining = ref(null);

const formattedDate = computed(() => format(props.date, "dd.MM.yyyy"));
const formattedTime = computed(() => format(props.date, "HH:mm"));

const closeNotify = () => {
    clearTimeout(timer.value);
    emit("close", props.item);
};

const startTimer = () => {
    start.value = Date.now();
    timer.value = setTimeout(() => {
        closeNotify();
    }, props.duration);
};

const pauseTimer = () => {
    clearTimeout(timer.value);
    remaining.value = props.duration - (Date.now() - start.value);
};

const resumeTimer = () => {
    start.value = Date.now();
    clearTimeout(timer.value);
    timer.value = setTimeout(() => {
        closeNotify();
    }, remaining.value);
};

const notificationStyles = computed(() => {
    const styles = {};

    if (props.color) {
        styles.borderColor = props.color;
        styles.backgroundColor = pSBC(0.85, props.color);
    }

    return styles;
});

onMounted(() => {
    if (props.withTimer && props.duration) {
        startTimer();
    }
});
</script>

<template>
    <div
        class="base-notification"
        :style="notificationStyles"
        @mouseenter="withTimer && pauseTimer"
        @mouseleave="withTimer && resumeTimer"
    >
        <div class="base-notification__header">
            <div v-if="icon" class="base-notification__icon">
                <slot name="NotificationIcon">
                    <BaseNotificationIcon :value="icon" :color="color" />
                </slot>
            </div>
            <div class="base-notification__content">
                <time class="base-notification__datetime">
                    {{ `${formattedTime}  ${formattedDate}` }}
                </time>
                <div v-if="title" class="base-notification__title">
                    <slot name="title">{{ title }}</slot>
                </div>
            </div>
        </div>
        <div v-if="text" class="base-notification__text">
            <slot name="text">{{ text }}</slot>
        </div>
        <button
            class="base-notification__cross"
            aria-label="Закрыть уведомление"
            @click="closeNotify"
        ></button>
    </div>
</template>

<style lang="scss" scoped>
.base-notification {
    position: relative;

    display: flex;

    flex-direction: column;

    padding: 12px 12px 15px;

    background-color: var(--c-background);
    border: 1px solid;
    border-radius: var(--default-medium-border-radius);
    box-shadow: var(--default-box-shadow);

    &__header {
        display: flex;

        min-width: 100%;
    }

    &__title {
        margin: 1px 0 3px;

        font-size: var(--default-text-size);
    }

    &__content {
        font-size: 11px;
    }

    &__datetime {
        font-size: 10px;
        line-height: 120%;
        color: var(--c-neutral-70);
    }

    &__icon {
        align-self: flex-start;

        width: 20px;
        margin-top: 3px;
        margin-right: 15px;

        text-align: center;
    }

    &__text {
        display: flex;

        flex-direction: column;

        gap: 8px;

        width: 100%;
        padding-top: 12px;

        font-size: var(--default-small-text-size);
        line-height: 160%;

        border-top: 1px solid v-bind(color);
    }

    &__cross {
        position: absolute;
        top: 10px;
        right: 10px;

        width: 15px;
        height: 15px;
        padding: 0;
        margin: 0;

        cursor: pointer;

        background: none !important;
        border: 0;
        opacity: 0.7;

        &:hover {
            opacity: 0.8;
        }

        &::before,
        &::after {
            position: absolute;
            top: 0;
            left: 7.5px;

            width: 2px;
            height: 15px;

            content: " ";

            background-color: var(--c-neutral-80);
        }

        &::before {
            transform: rotate(45deg);
        }

        &::after {
            transform: rotate(-45deg);
        }
    }

    :slotted(a) {
        text-decoration: underline;

        &:hover {
            color: v-bind(color);
        }
    }
}
</style>
